/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import UserService from '../../utilities/services/user-service';
import { Modal } from '../modal/modal';
import { User } from "../../models/interfaces/user";
import { Organization } from '../../models/interfaces/organization';
import { SymmioAccessType } from '../../models/enumerations/symmio-access-type';
import PortalProgressBar from '../portal-progress-bar/portal-progress-bar';
import { UserStatus } from '../../models/enumerations/user-status';
import { ReactComponent as UserCircleIcon } from "../../assets/icons/solid/icon_default-profile.svg";
import { OrganizationSubscription } from '../../models/interfaces/organization-subscription';
import AddOnRemovalsService from '../../utilities/services/add-on-removal-service';
import { AddOns } from '../../models/enumerations/add-ons';
import moment from 'moment';
import { AddOnRemoval } from '../../models/interfaces/add-on-removal';
import { Loader } from '../loader/loader';
import { CheckboxTypeInput } from '../forms/checkbox-type-input';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BillingService from '../../utilities/services/billing-service';
import { createLogger } from '../../utilities/logger';

const log = createLogger("LicenseRemovalModal");

const COMPONENT_CLASS = "manage-users-modal";

interface LicenseRemovalModalProps {
    openLicenseRemoval: boolean;
    setOpenLicenseRemoval: React.Dispatch<React.SetStateAction<boolean>>;
    isLoading: boolean;
    organization: Organization;
    hideClose?: boolean;
    deactivationAmount?: number;
    removeAtFutureDate: boolean;
    organizationSubscription?: OrganizationSubscription;
    handleAddOnRemove?: Function;
    addOnAmountToBeRemoved?: number;
    setAddOnRemovedModalOpen?: Function;
    setRemovedAddOnInfo?: Function;
    onSubmit?: Function;
}

const LicenseRemovalModal: React.FC<LicenseRemovalModalProps> = (props) => {
    const {
        openLicenseRemoval,
        setOpenLicenseRemoval,
        isLoading,
        organization,
        hideClose,
        deactivationAmount,
        removeAtFutureDate,
        organizationSubscription,
        handleAddOnRemove,
        addOnAmountToBeRemoved,
        setAddOnRemovedModalOpen,
        setRemovedAddOnInfo,
        onSubmit,
    } = props;

    const { state } = useAuthState();
    const [loading, setLoading] = useState<boolean>(false);
    const [searchedUsers, setSearchedUsers] = useState<User[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [userIdsToRemoveLicense, setUserIdsToRemoveLicense] = useState<string[]>([]);
    const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
    const [isAllChecked, setIsAllChecked] = useState<boolean>(false);
    const { t } = useTranslation();

    const {
        register,
    } = useForm<any>();

    // Modal is closed - reset everything
    useEffect(() => {
        setUserIdsToRemoveLicense([]);
    }, [openLicenseRemoval]);

    useEffect(() => {
        const fetchUsers = async () => {
            if (state.user?.organizationId) {
                const allUsers = await UserService.getAllByOrganizationId(state.user.organizationId);
                let filteredUsers = allUsers.filter((user) => user.id !== organization.accountHolderId && user.symmioAccess === SymmioAccessType.AppLicense);
                // Check if addOnRemovals exists & contains users
                if (organizationSubscription) {
                    await AddOnRemovalsService.getBy([{
                        field: "organizationSubscriptionId",
                        operator: "==",
                        value: organizationSubscription.id,
                    },
                    {
                        field: "removalCompleted",
                        operator: "==",
                        value: false,
                    },
                    {
                        field: "addOn",
                        operator: "==",
                        value: AddOns.License,
                    }]).then((item: any) => {
                        if (item[0] && item[0].userIds && item[0].userIds.length > 0) {
                            filteredUsers = filteredUsers.filter((val) => !item[0].userIds!.includes(val.id!))
                        }
                    })

                }

                setUsers(filteredUsers);
                setSearchedUsers(filteredUsers);
            }
        }

        fetchUsers();
    }, [state.user?.organizationId]);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        if (!value) {
            setSearchedUsers(users);
        }
        else {
            const foundUsers = users.filter((user) =>
                user.email?.includes(value) ||
                user.firstName?.includes(value) ||
                user.lastName?.includes(value));

            setSearchedUsers(foundUsers);
        }
    }

    const handleRowClick = (userIdFromRow: string | undefined) => {
        if (userIdFromRow) {
            if (userIdsToRemoveLicense.includes(userIdFromRow)) {
                setUserIdsToRemoveLicense(userIdsToRemoveLicense.filter(userId => userId !== userIdFromRow));
            }
            else {
                setUserIdsToRemoveLicense([...userIdsToRemoveLicense, userIdFromRow]);
            }
        }
    };
    const handleRemoveSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        if (!checked && searchedUsers.length === userIdsToRemoveLicense.length) {
            setIsAllChecked(false);
        }
    }
    const selectAllUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setIsAllChecked(checked);
        if (checked) {
            let userIds: string[] = [];

            searchedUsers.forEach((user) => {
                if (user.id && user.id !== organization.accountHolderId && user.symmioAccess === SymmioAccessType.AppLicense) {
                    userIds.push(user.id);
                }
            });

            setUserIdsToRemoveLicense(userIds);
        }
        else {
            setUserIdsToRemoveLicense([]);
        }
    }

    const handleFutureLicenseDeactivation = async () => {
        setLoading(true);
        if (!organizationSubscription || !handleAddOnRemove || !addOnAmountToBeRemoved || !organizationSubscription.stripeAddOnSubscription) {
            log.warn("Future license removal blocked: missing subscription or add-on data", {
                organizationId: organization?.id,
                userIdCount: userIdsToRemoveLicense.length,
            });
            return;
        }

        try {
            const subDetails = await BillingService.getSubscriptionDetails({
                subscriptionId: organizationSubscription.stripeAddOnSubscription,
            });

            // Check if AddOnRemoval exists
            // If removalCompleted false, then this is an active addOnRemoval
            const existingAddOnRemoval = await AddOnRemovalsService.getBy([{
                field: "organizationSubscriptionId",
                operator: "==",
                value: organizationSubscription.id,
            },
            {
                field: "removalCompleted",
                operator: "==",
                value: false,
            },
            {
                field: "addOn",
                operator: "==",
                value: AddOns.License,
            }])

            if (existingAddOnRemoval && existingAddOnRemoval.length > 0) {
                // update the existing addOnRemoval
                existingAddOnRemoval[0].userIds = [...existingAddOnRemoval[0].userIds!, ...userIdsToRemoveLicense]

                await AddOnRemovalsService.save(existingAddOnRemoval[0], state.user!);

            }
            else {

                // Create the future removal object
                const addOnRemoval = {
                    addOn: AddOns.License,
                    removalCompleted: false,
                    userIds: userIdsToRemoveLicense,
                    billingCycleEnd: moment(new Date(subDetails.currentPeriodEnd * 1000)).toISOString(),
                    organizationSubscriptionId: organizationSubscription.id
                } as AddOnRemoval

                // Save the future removal object
                await AddOnRemovalsService.add(addOnRemoval, state.user!);
            }

            if (setRemovedAddOnInfo) {
                setRemovedAddOnInfo({
                    name: AddOns.License,
                    amountToBeRemoved: Number(addOnAmountToBeRemoved),
                    amountManaged: userIdsToRemoveLicense.length,
                    billingCycleEnd: moment(new Date(subDetails.currentPeriodEnd * 1000)).format('MMMM D Y')
                })
            }

            handleAddOnRemove().then(() => {
                setOpenLicenseRemoval(false);
                // This must be passed as a prop if you are using future deactivation
                if (setAddOnRemovedModalOpen) {
                    setAddOnRemovedModalOpen(true);
                }
                setLoading(false);
            }).catch((e: any) => {
                log.error("Failed to complete future license removal", {
                    exception: e,
                    organizationId: organization?.id,
                    subjectUserIds: userIdsToRemoveLicense,
                });
                setLoading(false);
            });
        } catch (e) {
            log.error("Failed to schedule future license removal", {
                exception: e,
                organizationId: organization?.id,
                subjectUserIds: userIdsToRemoveLicense,
            });
            setLoading(false);
        }
    }

    const handleLicenseRemoval = async () => {
        try {
            await UserService.removeLicenses(userIdsToRemoveLicense, state.user);
            log.info("License(s) removed", {
                subjectUserIds: userIdsToRemoveLicense,
                organizationId: organization?.id,
                licenseCount: organization?.mySymmioLicenseCount,
            });
            onSubmit && onSubmit();
        } catch (e) {
            log.error("Failed to remove license(s)", {
                exception: e,
                subjectUserIds: userIdsToRemoveLicense,
                organizationId: organization?.id,
            });
        }
    }

    useEffect(() => {
        if (organization?.mySymmioLicenseCount &&
            organization?.mySymmioLicense &&
            userIdsToRemoveLicense.length >= (deactivationAmount ? deactivationAmount : (organization.mySymmioLicenseCount - organization.mySymmioLicense))) {
            setIsSubmitButtonDisabled(false);
        } else {
            setIsSubmitButtonDisabled(true);
        }
    }, [organization.mySymmioLicense, organization.mySymmioLicenseCount, userIdsToRemoveLicense]);
    return (
        <Modal
            isOpen={openLicenseRemoval}
            isLoading={isLoading}
            onClose={hideClose ? undefined : setOpenLicenseRemoval}
            title={t('licenseUserRemoval.common.manageAccount')}
            defaultModalActions={true}
            onSubmit={async () => {
                if (removeAtFutureDate) {
                    handleFutureLicenseDeactivation()
                }
                else {
                    handleLicenseRemoval();
                }

            }}
            onCancel={!hideClose ? () => setOpenLicenseRemoval(false) : undefined}
            submitButtonText={t('buttons.btn_removeLicense')}
            submitDisabled={isSubmitButtonDisabled}>
            <Loader isVisible={loading} />
            <div className={COMPONENT_CLASS}>
                {organization.mySymmioLicense && organization.mySymmioLicenseCount &&
                    <div className="my-6">
                        <PortalProgressBar
                            total={organization.mySymmioLicense}
                            currentProgress={organization.mySymmioLicenseCount}
                            headerText={t('licenseUserRemoval.usedLicenseProgress', { mySymmioLicenseCount: organization.mySymmioLicenseCount, mySymmioLicenseLimit: organization.mySymmioLicense })} />
                    </div>
                }

                {organization.mySymmioLicenseCount && organization.mySymmioLicense &&
                    <h2>{t('licenseUserRemoval.licensesToRemove', { count: deactivationAmount ? deactivationAmount : (organization.mySymmioLicenseCount - organization.mySymmioLicense) })}</h2>
                }

                <input className='my-6' type='search' onChange={handleSearch} placeholder={t('misc.search')} />

                <div className={`${COMPONENT_CLASS}__user-list`}>
                    <table>
                        <thead>
                            <tr>
                                <th></th>
                                <th><span>{t('licenseUserRemoval.common.name')}</span></th>
                                <th><span>{t('licenseUserRemoval.common.group')}</span></th>
                                <th><span>{t('licenseUserRemoval.common.status')}</span></th>
                                <th>
                                    <label>
                                        <div>
                                            <CheckboxTypeInput
                                                id="selectAllUsers"
                                                registerHook={register}
                                                registerOptions={{
                                                    onChange: selectAllUsers,
                                                }}
                                                checked={isAllChecked} />
                                        </div>
                                        {t('licenseUserRemoval.removeLicense_caps')}
                                    </label>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {searchedUsers.map((user) => {
                                return (
                                    <tr key={user.id} className={user.symmioAccess !== SymmioAccessType.AppLicense ? "deactivated" : ""}
                                        onClick={() => {
                                            if (user.id !== organization.accountHolderId && user.symmioAccess === SymmioAccessType.AppLicense) {
                                                handleRowClick(user.id)
                                            }
                                        }}>
                                        <td className={`${COMPONENT_CLASS}__user-photo`}>
                                            {user.profileImage && (
                                                <img
                                                    src={user.profileImage}
                                                    alt={`${user.firstName} ${user.lastName}`}
                                                />
                                            )}
                                            {!user.profileImage && (
                                                <UserCircleIcon aria-hidden="true" />
                                            )}
                                        </td>

                                        <td>
                                            {user.firstName != null && user.lastName != null && (
                                                <span>
                                                    <span className="font-bold">{`${user.firstName} ${user.lastName}`}</span>
                                                    <br />
                                                    <span className="email">{user.email}</span>
                                                </span>
                                            )}

                                            {(user.firstName == null || user.lastName == null) && (
                                                <span>{user.email}</span>
                                            )}
                                        </td>

                                        <td>{user.groupName || t('licenseUserRemoval.common.noGroup')}</td>

                                        <td>{UserStatus[user.status]}</td>

                                        <td>
                                            {user.symmioAccess !== SymmioAccessType.AppLicense
                                                ?
                                                (<div>
                                                    <CheckboxTypeInput
                                                        id={"user-id"}
                                                        registerHook={register}
                                                        registerOptions={{
                                                            disabled: true,
                                                        }}
                                                    /></div>)
                                                :
                                                (user.id !== organization.accountHolderId
                                                    ?
                                                    <div className='flex flex-row items-center gap-2'>
                                                        <div>
                                                            <CheckboxTypeInput
                                                                id={"user-id"}
                                                                registerHook={register}
                                                                registerOptions={{
                                                                    onChange: handleRemoveSelectAll
                                                                }}
                                                                checked={user.id && userIdsToRemoveLicense.length > 0 ? userIdsToRemoveLicense.includes(user.id) : false} />
                                                        </div>
                                                        {t('licenseUserRemoval.removeLicense_low')}
                                                    </div>
                                                    :
                                                    <div><strong>{t('licenseUserRemoval.common.accountHolder')}</strong></div>
                                                )

                                            }
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
                <div className='mt-16 mb-6 text-right' dangerouslySetInnerHTML={{ __html: t('licenseUserRemoval.licensesToBeRemoved', { count: userIdsToRemoveLicense.length }) }}></div>
            </div>
        </Modal>
    );
};

export default LicenseRemovalModal;